<template>
  <div class="admin">
    <div class="row">
      <div class="col-12">
        <div class="card shadow">
          <div class="card-header bg-warning text-dark d-flex justify-content-between align-items-center">
            <h3 class="mb-0">
              <i class="bi bi-gear"></i>
              Администрирование
            </h3>
            <div class="d-flex align-items-center">
              <span class="badge bg-success me-2">
                <i class="bi bi-shield-check"></i> Авторизован
              </span>
              <button @click="logout" class="btn btn-outline-dark btn-sm">
                <i class="bi bi-box-arrow-right"></i> Выход
              </button>
            </div>
          </div>

          <div class="card-body p-4">
            <!-- Статистика -->
            <div class="row mb-4">
              <div class="col-md-4">
                <div class="card bg-primary text-white">
                  <div class="card-body text-center">
                    <div class="h2">{{ stats.total || 0 }}</div>
                    <div>Всего паспортов</div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card bg-success text-white">
                  <div class="card-body text-center">
                    <div class="h2">{{ stats.filled || 0 }}</div>
                    <div>Заполненных</div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card bg-secondary text-white">
                  <div class="card-body text-center">
                    <div class="h2">{{ stats.empty || 0 }}</div>
                    <div>Пустых</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Управление -->
            <div class="row mb-4">
              <div class="col-md-12">
                <div class="card">
                  <div class="card-header">
                    <h5 class="mb-0">Действия</h5>
                  </div>
                  <div class="card-body">
                    <button @click="refreshStats" class="btn btn-outline-primary btn-sm mb-2 me-2">
                      <i class="bi bi-arrow-clockwise"></i>
                      Обновить статистику
                    </button>
                    <button @click="exportData" class="btn btn-outline-success btn-sm mb-2 me-2">
                      <i class="bi bi-download"></i>
                      Экспорт CSV
                    </button>
                    <button @click="showRecentPassports" class="btn btn-outline-info btn-sm mb-2">
                      <i class="bi bi-clock"></i>
                      Недавние паспорта
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-secondary btn-sm mb-2 ms-2"
                      @click="showQr = !showQr"
                    >
                      <i class="bi bi-qr-code"></i>
                      {{ showQr ? 'Скрыть QR' : 'Показать QR' }}
                    </button>
                    <div class="d-inline-flex align-items-center ms-2 mb-2 gap-2">
                      <label for="admin_qr_domain" class="small text-muted text-nowrap mb-0">QR-домен:</label>
                      <select id="admin_qr_domain" class="form-select form-select-sm" v-model="qrBaseUrl">
                        <option v-for="domain in qrDomains" :key="domain.value" :value="domain.value">
                          {{ domain.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Массовые операции: серийные номера и экспорт пакета -->
            <div class="card mb-4">
              <div class="card-header">
                <h5 class="mb-0">Серийные номера и экспорт пакета</h5>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Идентификаторы (по одному в строке):</label>
                    <textarea
                      class="form-control font-monospace"
                      rows="4"
                      v-model="bulkIdentifiers"
                      placeholder="LPG0000B&#10;LPG0000C"
                    ></textarea>
                    <div class="form-text">
                      Распознано: <strong>{{ parsedBulkIdentifiers.length }}</strong>. Порядок задаёт последовательность номеров.
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Стартовый серийный номер:</label>
                      <input type="number" class="form-control" v-model.number="bulkSerialStart" placeholder="262001001" min="0">
                    </div>
                    <div class="mb-3">
                      <label class="form-label">Партия (batch):</label>
                      <input type="text" class="form-control" v-model="bulkBatch" placeholder="RONNIE-2026-05">
                    </div>
                    <div class="mb-2">
                      <label class="form-label">PDF в пакете:</label>
                      <select class="form-select" v-model.number="bulkLabelsPerPdf">
                        <option :value="0">Один файл</option>
                        <option :value="100">Разбить по 100</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div class="d-flex flex-wrap gap-2">
                  <button @click="assignSerial" class="btn btn-warning btn-sm" :disabled="bulkBusy || !parsedBulkIdentifiers.length">
                    <i class="bi bi-hash"></i>
                    Присвоить серийные номера
                  </button>
                  <button @click="downloadPackageByIdentifiers" class="btn btn-primary btn-sm" :disabled="bulkBusy || !parsedBulkIdentifiers.length">
                    <i class="bi bi-download"></i>
                    Скачать пакет по списку
                  </button>
                  <span class="vr mx-1"></span>
                  <button @click="downloadPackageByBatch" class="btn btn-outline-primary btn-sm" :disabled="bulkBusy || !bulkBatch.trim()">
                    <i class="bi bi-download"></i>
                    Скачать пакет по партии
                  </button>
                  <span v-if="bulkBusy" class="align-self-center text-muted small">
                    <span class="spinner-border spinner-border-sm me-1"></span>Выполняется…
                  </span>
                </div>
                <div v-if="bulkMessage" class="alert mt-3 mb-0" :class="bulkError ? 'alert-danger' : 'alert-success'">
                  {{ bulkMessage }}
                </div>
              </div>
            </div>

            <!-- Массовое заполнение характеристик баллона по партии -->
            <div class="card mb-4">
              <div class="card-header">
                <h5 class="mb-0">Характеристики баллона (на партию)</h5>
              </div>
              <div class="card-body">
                <p class="text-muted small mb-3">
                  Записываются всем паспортам указанной партии. Пустые поля не меняются.
                  Рабочее давление дублируется в «Давление (атм)».
                </p>
                <div class="row">
                  <div class="col-md-4 mb-3">
                    <label class="form-label"><strong>Партия (batch):</strong></label>
                    <input type="text" class="form-control" v-model="cylSpec.batch" placeholder="RONNIE-2026-05">
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Модель:</label>
                    <input type="text" class="form-control" v-model="cylSpec.model" placeholder="YSP23.9/10">
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Материал корпуса:</label>
                    <input type="text" class="form-control" v-model="cylSpec.material" placeholder="HP295">
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Стандарт:</label>
                    <input type="text" class="form-control" v-model="cylSpec.standard" placeholder="GB 5842">
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Рабочее давление, МПа:</label>
                    <input type="number" step="0.01" class="form-control" v-model="cylSpec.working_pressure_mpa" placeholder="2.1">
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Испытательное давление, МПа:</label>
                    <input type="number" step="0.01" class="form-control" v-model="cylSpec.hydro_test_pressure_mpa" placeholder="3.2">
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Давление герметичности, МПа:</label>
                    <input type="number" step="0.01" class="form-control" v-model="cylSpec.tightness_pressure_mpa" placeholder="2.1">
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Наружный диаметр, мм:</label>
                    <input type="number" step="0.1" class="form-control" v-model="cylSpec.outer_diameter_mm" placeholder="280">
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Толщина стенки, мм:</label>
                    <input type="number" step="0.01" class="form-control" v-model="cylSpec.wall_thickness_mm" placeholder="2.1">
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Заводской № партии:</label>
                    <input type="text" class="form-control" v-model="cylSpec.factory_batch" placeholder="001262006">
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Дата изготовления:</label>
                    <input type="text" class="form-control" v-model="cylSpec.manufacture_date" placeholder="2026-03">
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Лицензия:</label>
                    <input type="text" class="form-control" v-model="cylSpec.license_number" placeholder="TS2237T23-2028">
                  </div>
                  <div class="col-md-12 mb-3">
                    <label class="form-label">Термообработка:</label>
                    <input type="text" class="form-control" v-model="cylSpec.heat_treatment" placeholder="Отжиг для снятия напряжений, 760±20°C, 5 мин">
                  </div>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <button @click="prefillCylSpec" class="btn btn-outline-secondary btn-sm" type="button">
                    Подставить значения Ronnie
                  </button>
                  <button @click="assignCylinderSpecs" class="btn btn-warning btn-sm" :disabled="cylSpecBusy || !cylSpec.batch.trim()">
                    <span v-if="cylSpecBusy" class="spinner-border spinner-border-sm me-1"></span>
                    Применить к партии
                  </button>
                </div>
                <div v-if="cylSpecMessage" class="alert mt-3 mb-0" :class="cylSpecError ? 'alert-danger' : 'alert-success'">
                  {{ cylSpecMessage }}
                </div>
              </div>
            </div>

            <!-- Импорт результатов гидроиспытаний из xlsx поставщика -->
            <div class="card mb-4">
              <div class="card-header">
                <h5 class="mb-0">Импорт гидроиспытаний (xlsx)</h5>
              </div>
              <div class="card-body">
                <p class="text-muted small mb-2">
                  Файл поставщика (протокол гидроиспытаний). Данные (давление, дата, результат, выдержка)
                  записываются в паспорта по совпадению <strong>серийного номера</strong> (钢瓶编号 в файле завода).
                </p>
                <div class="input-group">
                  <input type="file" class="form-control" accept=".xlsx,.xls" @change="onTestFileSelect" ref="testFileInput">
                  <button @click="importTestData" class="btn btn-success" :disabled="!testFile || importingTest">
                    <span v-if="importingTest" class="spinner-border spinner-border-sm me-1"></span>
                    <i v-else class="bi bi-upload"></i>
                    Импортировать
                  </button>
                </div>
                <div v-if="importMessage" class="alert mt-3 mb-0" :class="importError ? 'alert-danger' : 'alert-success'">
                  {{ importMessage }}
                </div>
              </div>
            </div>

            <!-- Поиск и фильтры -->
            <div class="card mb-4">
              <div class="card-header">
                <h5 class="mb-0">Поиск паспортов</h5>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-4 col-lg-3 mb-3">
                    <label class="form-label">Идентификатор:</label>
                    <input type="text" class="form-control" v-model="filters.identifier" placeholder="LPG…" @keyup.enter="applyFilters">
                  </div>
                  <div class="col-md-4 col-lg-3 mb-3">
                    <label class="form-label">Серийный номер:</label>
                    <input type="text" class="form-control" v-model="filters.serial_number" placeholder="262001001" @keyup.enter="applyFilters">
                  </div>
                  <div class="col-md-4 col-lg-2 mb-3">
                    <label class="form-label">Газ:</label>
                    <select v-model="filters.gas" @change="applyFilters" class="form-select">
                      <option value="">Все</option>
                      <option v-for="gas in gases" :key="gas" :value="gas">{{ gas }}</option>
                    </select>
                  </div>
                  <div class="col-md-4 col-lg-2 mb-3">
                    <label class="form-label">Давление (атм):</label>
                    <input type="number" step="0.01" min="0" class="form-control" v-model="filters.pressure" placeholder="150" @keyup.enter="applyFilters">
                  </div>
                  <div class="col-md-4 col-lg-2 mb-3">
                    <label class="form-label">Объём (л):</label>
                    <input type="number" step="0.01" min="0" class="form-control" v-model="filters.volume" placeholder="40" @keyup.enter="applyFilters">
                  </div>
                  <div class="col-md-4 col-lg-3 mb-3">
                    <label class="form-label">На странице:</label>
                    <select v-model="pageSize" @change="applyFilters" class="form-select">
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                </div>
                <div class="d-flex gap-2">
                  <button @click="applyFilters" class="btn btn-primary btn-sm">
                    <i class="bi bi-search"></i> Найти
                  </button>
                  <button @click="resetFilters" class="btn btn-outline-secondary btn-sm" :disabled="!hasActiveFilters">
                    Сбросить
                  </button>
                </div>
              </div>
            </div>

            <!-- Таблица паспортов -->
            <div class="card">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0">Список паспортов</h5>
                <div class="d-flex align-items-center gap-2">
                  <button
                    v-if="selectedIds.length"
                    @click="deleteSelected"
                    class="btn btn-danger btn-sm"
                    :disabled="deletingSelected"
                  >
                    <span v-if="deletingSelected" class="spinner-border spinner-border-sm me-1"></span>
                    <i v-else class="bi bi-trash"></i>
                    Удалить выбранные ({{ selectedIds.length }})
                  </button>
                  <span class="badge bg-secondary">{{ pagination.total || 0 }} всего</span>
                </div>
              </div>
              
              <div v-if="loading" class="card-body text-center">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Загрузка...</span>
                </div>
                <p class="mt-2">Загрузка данных...</p>
              </div>

              <div v-else-if="passports.length === 0" class="card-body text-center text-muted">
                <i class="bi bi-inbox display-1 mb-3"></i>
                <p>Паспорта не найдены</p>
              </div>

              <div v-else class="table-responsive">
                <table class="table table-hover mb-0">
                  <thead class="table-light">
                    <tr>
                      <th style="width: 36px;">
                        <input
                          type="checkbox"
                          class="form-check-input"
                          :checked="allOnPageSelected"
                          :indeterminate.prop="someOnPageSelected && !allOnPageSelected"
                          @change="toggleSelectAllOnPage($event.target.checked)"
                          title="Выбрать все на странице"
                        >
                      </th>
                      <th>ID</th>
                      <th>Идентификатор</th>
                      <th v-if="showQr">QR</th>
                      <th>Серийный номер</th>
                      <th>Давление</th>
                      <th>Объем</th>
                      <th>Статус</th>
                      <th>Создан</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="passport in passports" :key="passport.id" :class="{ 'table-active': isSelected(passport) }">
                      <td>
                        <input
                          type="checkbox"
                          class="form-check-input"
                          :checked="isSelected(passport)"
                          @change="toggleSelect(passport, $event.target.checked)"
                        >
                      </td>
                      <td>{{ passport.id }}</td>
                      <td>
                        <span class="badge bg-primary font-monospace">{{ passport.identifier }}</span>
                      </td>
                      <td v-if="showQr" class="qr-cell">
                        <QrCode
                          :value="qrLink(passport.identifier)"
                          :size="72"
                          :margin="1"
                          errorCorrectionLevel="M"
                          :fileName="`qr-${passport.identifier}.png`"
                          :caption="passport.identifier"
                        />
                      </td>
                      <td>
                        <span v-if="passport.serial_number" class="text-truncate" style="max-width: 150px;">
                          {{ passport.serial_number }}
                        </span>
                        <span v-else class="text-muted">—</span>
                      </td>
                      <td>
                        <span v-if="passport.pressure && passport.pressure > 0">
                          {{ passport.pressure }} бар
                        </span>
                        <span v-else class="text-muted">—</span>
                      </td>
                      <td>
                        <span v-if="passport.volume && passport.volume > 0">
                          {{ passport.volume }} л
                        </span>
                        <span v-else class="text-muted">—</span>
                      </td>
                      <td>
                        <span v-if="isFilledPassport(passport)" class="badge bg-success">Заполнен</span>
                        <span v-else class="badge bg-secondary">Пустой</span>
                      </td>
                      <td>
                        <small>{{ formatDate(passport.created_at) }}</small>
                      </td>
                      <td>
                        <div class="btn-group" role="group">
                          <router-link 
                            :to="`/${passport.identifier}`" 
                            class="btn btn-sm btn-outline-primary"
                            target="_blank"
                            title="Просмотр паспорта"
                          >
                            Просмотр
                          </router-link>
                          <router-link 
                            :to="`/${passport.identifier}?edit=true`" 
                            class="btn btn-sm btn-outline-warning"
                            title="Редактировать паспорт"
                          >
                            <i class="bi bi-pencil"></i> Редактировать
                          </router-link>
                          <button 
                            @click="deletePassport(passport)"
                            class="btn btn-sm btn-outline-danger"
                            title="Удалить паспорт"
                          >
                            <i class="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Пагинация -->
              <div v-if="pagination.pages > 1" class="card-footer">
                <nav>
                  <ul class="pagination pagination-sm justify-content-center mb-0">
                    <li class="page-item" :class="{ disabled: pagination.page <= 1 }">
                      <button @click="goToPage(pagination.page - 1)" class="page-link">Назад</button>
                    </li>
                    
                    <li 
                      v-for="page in visiblePages" 
                      :key="page"
                      class="page-item"
                      :class="{ active: page === pagination.page }"
                    >
                      <button @click="goToPage(page)" class="page-link">{{ page }}</button>
                    </li>
                    
                    <li class="page-item" :class="{ disabled: pagination.page >= pagination.pages }">
                      <button @click="goToPage(pagination.page + 1)" class="page-link">Далее</button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import QrCode from '../components/QrCode.vue'
import { QR_DOMAINS, DEFAULT_QR_DOMAIN, qrLink } from '../qrDomains'

export default {
  name: 'Admin',
  components: { QrCode },
  data() {
    return {
      passports: [],
      gases: [],
      stats: {},
      loading: true,
      filters: {
        identifier: '',
          serial_number: '',
        gas: '',
        pressure: '',
        volume: ''
      },
      pageSize: 50,
      showQr: true,
      qrDomains: QR_DOMAINS,
      qrBaseUrl: DEFAULT_QR_DOMAIN,
      bulkIdentifiers: '',
      bulkSerialStart: null,
      bulkBatch: '',
      bulkLabelsPerPdf: 0,
      bulkBusy: false,
      bulkMessage: '',
      bulkError: false,
      selectedIds: [],
      deletingSelected: false,
      testFile: null,
      importingTest: false,
      importMessage: '',
      importError: false,
      cylSpec: {
        batch: '', model: '', material: '', standard: '',
        working_pressure_mpa: '', hydro_test_pressure_mpa: '', tightness_pressure_mpa: '',
        outer_diameter_mm: '', wall_thickness_mm: '',
        factory_batch: '', manufacture_date: '', license_number: '', heat_treatment: ''
      },
      cylSpecBusy: false,
      cylSpecMessage: '',
      cylSpecError: false,
      pagination: {
        page: 1,
        pages: 1,
        total: 0
      }
    }
  },
  computed: {
    parsedBulkIdentifiers() {
      return (this.bulkIdentifiers || '')
        .split(/[\s,;]+/)
        .map(s => s.trim())
        .filter(Boolean)
    },
    hasActiveFilters() {
      return Object.values(this.filters).some(v => `${v}`.trim() !== '')
    },
    allOnPageSelected() {
      return this.passports.length > 0 &&
        this.passports.every(p => this.selectedIds.includes(p.identifier))
    },
    someOnPageSelected() {
      return this.passports.some(p => this.selectedIds.includes(p.identifier))
    },
    visiblePages() {
      const current = this.pagination.page
      const total = this.pagination.pages
      const delta = 2
      
      const range = []
      const rangeWithDots = []
      
      for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
        range.push(i)
      }
      
      if (current - delta > 2) {
        rangeWithDots.push(1, '...')
      } else {
        rangeWithDots.push(1)
      }
      
      rangeWithDots.push(...range)
      
      if (current + delta < total - 1) {
        rangeWithDots.push('...', total)
      } else if (total > 1) {
        rangeWithDots.push(total)
      }
      
      return rangeWithDots.filter((item, index, arr) => arr.indexOf(item) === index)
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    qrLink(identifier) {
      return qrLink(identifier, this.qrBaseUrl)
    },
    async loadData() {
      await Promise.all([
        this.loadPassports(),
        this.loadStats(),
        this.loadGases()
      ])
    },

    async loadPassports() {
      this.loading = true
      this.selectedIds = []

      try {
        const params = {
          page: this.pagination.page,
          limit: this.pageSize
        }

        for (const [key, value] of Object.entries(this.filters)) {
          if (`${value}`.trim() !== '') {
            params[key] = `${value}`.trim()
          }
        }

        const response = await axios.get('/api/passports', { params })
        
        if (response.data.success) {
          this.passports = response.data.data
          this.pagination = response.data.pagination
        }
      } catch (error) {
        console.error('Ошибка загрузки паспортов:', error)
      } finally {
        this.loading = false
      }
    },

    async loadGases() {
      try {
        const response = await axios.get('/api/gases')
        if (response.data.success) {
          this.gases = response.data.data
        }
      } catch (error) {
        console.error('Ошибка загрузки списка газов:', error)
      }
    },

    async loadStats() {
      try {
        const response = await axios.get('/api/stats')
        if (response.data.success) {
          this.stats = response.data.data
        }
      } catch (error) {
        console.error('Ошибка загрузки статистики:', error)
        // Fallback к значениям по умолчанию
        this.stats = {
          total: 0,
          filled: 0,
          empty: 0
        }
      }
    },

    async applyFilters() {
      this.pagination.page = 1
      await this.loadPassports()
    },

    async resetFilters() {
      this.filters = { identifier: '', serial_number: '', gas: '', pressure: '', volume: '' }
      this.pagination.page = 1
      await this.loadPassports()
    },

    async goToPage(page) {
      if (page >= 1 && page <= this.pagination.pages) {
        this.pagination.page = page
        await this.loadPassports()
      }
    },

    async refreshStats() {
      await this.loadStats()
      await this.loadPassports()
    },

    async exportData() {
      try {
        const response = await axios.get('/api/passports', {
          params: { limit: 10000 }
        })
        
        if (response.data.success) {
          const headers = ['ID', 'Идентификатор', 'Серийный номер', 'Давление', 'Объем', 'Описание', 'Создан', 'Обновлен']
          const rows = response.data.data.map(passport => [
            passport.id,
            passport.identifier,
            passport.serial_number || '',
            passport.pressure || '',
            passport.volume || '',
            passport.description || '',
            this.formatDate(passport.created_at),
            this.formatDate(passport.updated_at)
          ])

          const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(field => `"${field}"`).join(','))
          ].join('\n')

          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
          const link = document.createElement('a')
          
          if (link.download !== undefined) {
            const url = URL.createObjectURL(blob)
            link.setAttribute('href', url)
            link.setAttribute('download', `gas-cylinders-export-${new Date().toISOString().split('T')[0]}.csv`)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          }
        }
      } catch (error) {
        console.error('Ошибка экспорта данных:', error)
      }
    },

    showRecentPassports() {
      // Сортировка по дате создания
      this.pagination.page = 1
      this.loadPassports()
    },

    isFilledPassport(passport) {
      return passport.serial_number || 
             (passport.pressure && passport.pressure > 0) || 
             (passport.volume && passport.volume > 0) || 
             passport.description
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    logout() {
      localStorage.removeItem('adminAuth')
      this.$router.push('/')
    },

    async assignSerial() {
      const identifiers = this.parsedBulkIdentifiers
      if (!identifiers.length) {
        return
      }
      if ((this.bulkSerialStart === null || this.bulkSerialStart === '') && !this.bulkBatch.trim()) {
        this.bulkError = true
        this.bulkMessage = 'Укажите стартовый серийный номер и/или партию'
        return
      }
      this.bulkBusy = true
      this.bulkError = false
      this.bulkMessage = ''
      try {
        const response = await axios.post('/api/assign-serial', {
          identifiers,
          serial_start: this.bulkSerialStart,
          batch: this.bulkBatch,
          adminAuth: localStorage.getItem('adminAuth')
        }, {
          headers: { 'X-Admin-Auth': localStorage.getItem('adminAuth') }
        })
        const { updated, notFound } = response.data.data
        this.bulkMessage = `Обновлено ${updated.length} паспортов`
          + (notFound.length ? `. Не найдено: ${notFound.join(', ')}` : '')
        await this.loadPassports()
      } catch (error) {
        this.bulkError = true
        this.bulkMessage = error.response?.data?.message || 'Ошибка при присвоении серийных номеров'
      } finally {
        this.bulkBusy = false
      }
    },

    async downloadPackageByIdentifiers() {
      if (!this.parsedBulkIdentifiers.length) {
        return
      }
      await this.downloadPackage({ identifiers: this.parsedBulkIdentifiers })
    },

    async downloadPackageByBatch() {
      if (!this.bulkBatch.trim()) {
        return
      }
      await this.downloadPackage({ batch: this.bulkBatch.trim() })
    },

    async downloadPackage(selector) {
      this.bulkBusy = true
      this.bulkError = false
      this.bulkMessage = ''
      try {
        const response = await axios.post('/api/export-package', {
          ...selector,
          labelsPerPdf: this.bulkLabelsPerPdf,
          baseUrl: this.qrBaseUrl,
          adminAuth: localStorage.getItem('adminAuth')
        }, {
          headers: { 'X-Admin-Auth': localStorage.getItem('adminAuth') },
          responseType: 'blob'
        })
        const url = URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `qr-package-${new Date().toISOString().split('T')[0]}.zip`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        this.bulkMessage = 'Пакет сформирован и скачан'
      } catch (error) {
        this.bulkError = true
        // ответ-ошибка приходит как Blob при responseType:blob — пытаемся прочитать текст
        let msg = 'Не удалось сформировать пакет'
        if (error.response?.data instanceof Blob) {
          try { msg = JSON.parse(await error.response.data.text()).message || msg } catch (e) { /* ignore */ }
        } else {
          msg = error.response?.data?.message || msg
        }
        this.bulkMessage = msg
      } finally {
        this.bulkBusy = false
      }
    },

    prefillCylSpec() {
      Object.assign(this.cylSpec, {
        model: 'YSP23.9/10',
        material: 'HP295',
        standard: 'GB 5842',
        working_pressure_mpa: 2.1,
        hydro_test_pressure_mpa: 3.2,
        tightness_pressure_mpa: 2.1,
        outer_diameter_mm: 280,
        wall_thickness_mm: 2.1,
        license_number: 'TS2237T23-2028',
        heat_treatment: 'Отжиг для снятия напряжений, 760±20°C, 5 мин'
      })
    },

    async assignCylinderSpecs() {
      if (!this.cylSpec.batch.trim()) {
        return
      }
      this.cylSpecBusy = true
      this.cylSpecError = false
      this.cylSpecMessage = ''
      try {
        const response = await axios.post('/api/assign-cylinder-specs', {
          ...this.cylSpec,
          adminAuth: localStorage.getItem('adminAuth')
        }, {
          headers: { 'X-Admin-Auth': localStorage.getItem('adminAuth') }
        })
        this.cylSpecMessage = response.data.message
        await this.loadPassports()
      } catch (error) {
        this.cylSpecError = true
        this.cylSpecMessage = error.response?.data?.message || 'Ошибка при заполнении характеристик'
      } finally {
        this.cylSpecBusy = false
      }
    },

    onTestFileSelect(event) {
      this.testFile = event.target.files[0] || null
      this.importMessage = ''
    },

    async importTestData() {
      if (!this.testFile) {
        return
      }
      this.importingTest = true
      this.importError = false
      this.importMessage = ''
      try {
        const form = new FormData()
        form.append('file', this.testFile)
        form.append('adminAuth', localStorage.getItem('adminAuth'))
        const response = await axios.post('/api/import-test-data', form, {
          headers: { 'X-Admin-Auth': localStorage.getItem('adminAuth') }
        })
        const d = response.data.data
        this.importMessage = response.data.message
          + (d.notFoundCount ? ` Не найдены (первые): ${d.notFound.join(', ')}` : '')
        this.testFile = null
        if (this.$refs.testFileInput) this.$refs.testFileInput.value = ''
        await this.loadPassports()
      } catch (error) {
        this.importError = true
        this.importMessage = error.response?.data?.message || 'Ошибка импорта файла'
      } finally {
        this.importingTest = false
      }
    },

    isSelected(passport) {
      return this.selectedIds.includes(passport.identifier)
    },

    toggleSelect(passport, checked) {
      if (checked) {
        if (!this.selectedIds.includes(passport.identifier)) {
          this.selectedIds.push(passport.identifier)
        }
      } else {
        this.selectedIds = this.selectedIds.filter(id => id !== passport.identifier)
      }
    },

    toggleSelectAllOnPage(checked) {
      const pageIds = this.passports.map(p => p.identifier)
      if (checked) {
        this.selectedIds = [...new Set([...this.selectedIds, ...pageIds])]
      } else {
        this.selectedIds = this.selectedIds.filter(id => !pageIds.includes(id))
      }
    },

    async deleteSelected() {
      if (!this.selectedIds.length) {
        return
      }
      if (!confirm(`Удалить выбранные паспорта (${this.selectedIds.length} шт.)?\n\nЭто действие нельзя отменить.`)) {
        return
      }
      this.deletingSelected = true
      try {
        const response = await axios.post('/api/passports/bulk-delete', {
          identifiers: this.selectedIds,
          adminAuth: localStorage.getItem('adminAuth')
        }, {
          headers: { 'X-Admin-Auth': localStorage.getItem('adminAuth') }
        })
        if (response.data.success) {
          alert(response.data.message)
          await this.loadStats()
          // если страница опустела и не первая — на предыдущую
          if (this.passports.length === this.selectedIds.length && this.pagination.page > 1) {
            this.pagination.page--
          }
          await this.loadPassports()
        }
      } catch (error) {
        console.error('Ошибка массового удаления:', error)
        alert(error.response?.data?.message || 'Ошибка при массовом удалении')
      } finally {
        this.deletingSelected = false
      }
    },

    async deletePassport(passport) {
      if (!confirm(`Вы уверены, что хотите удалить паспорт с идентификатором "${passport.identifier}"?\n\nЭто действие нельзя отменить.`)) {
        return
      }

      try {
        const response = await axios.delete(`/api/passport/${passport.identifier}`, {
          headers: { 'X-Admin-Auth': localStorage.getItem('adminAuth') }
        })
        
        if (response.data.success) {
          // Удаляем паспорт из списка
          this.passports = this.passports.filter(p => p.id !== passport.id)
          // Обновляем статистику
          await this.loadStats()
          // Если текущая страница пуста и не первая, переходим на предыдущую
          if (this.passports.length === 0 && this.pagination.page > 1) {
            this.pagination.page--
            await this.loadPassports()
          }
          alert('Паспорт успешно удален')
        }
      } catch (error) {
        console.error('Ошибка удаления паспорта:', error)
        alert(error.response?.data?.message || 'Ошибка при удалении паспорта')
      }
    }
  }
}
</script>

<style scoped>
.font-monospace {
  font-family: 'Courier New', monospace;
}

.text-truncate {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pagination .page-link {
  color: #0d6efd;
}

.pagination .page-item.active .page-link {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.qr-cell {
  min-width: 140px;
}

.bi-gear::before { content: "⚙️"; }
.bi-database::before { content: "🗄️"; }
.bi-arrow-clockwise::before { content: "🔄"; }
.bi-download::before { content: "⬇️"; }
.bi-clock::before { content: "🕐"; }
.bi-search::before { content: "🔍"; }
.bi-inbox::before { content: "📥"; }
.bi-shield-check::before { content: "✅"; }
.bi-box-arrow-right::before { content: "🚪"; }
.bi-qr-code::before { content: "🔳"; }
.bi-trash::before { content: "🗑️"; }
.bi-pencil::before { content: "✏️"; }
.bi-hash::before { content: "#️⃣"; }
</style>
